import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const img = (u) => (u && u.charAt(0) === "/" ? API + u : u || "");
const money = (n) => "$" + Number(n || 0).toFixed(2);

// Public hosted booking page: /s/:slug/book  (deep-link target for GHL + booking.js iframe).
// ?embed=1 renders chrome-less (for the embedded modal iframe).
export default function BookPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const [tenant, setTenant] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!slug) return;
    Promise.all([
      axios.get(`${API}/api/public/tenant/${slug}`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/api/public/tenant/${slug}/booking/config`).then((r) => r.data).catch(() => null),
    ]).then(([t, c]) => {
      setTenant(t);
      setCfg(c);
      setStatus(c && c.enabled ? "on" : "off");
    });
  }, [slug]);

  // Load embed.js once bookings are on — it powers the wizard + wires [data-morrcart-book] clicks.
  useEffect(() => {
    if (status !== "on") return;
    document.title = `Book an Event · ${tenant?.name || slug}`;
    const s = document.createElement("script");
    s.id = "morrcart-embed";
    s.src = `${API}/embed.js`;
    s.setAttribute("data-tenant", slug);
    s.setAttribute("data-morrcart-context", "hosted");
    document.body.appendChild(s);
    return () => {
      s.remove();
      document.querySelectorAll("[data-morrcart]").forEach((n) => n.remove());
      window.__morrcartLoaded = undefined;
      window.MorrCart = undefined;
    };
  }, [status, slug, tenant]);

  const brand = tenant?.primary_color || "#111";
  const packages = cfg?.packages || [];

  const shell = (children) => (
    <div style={{ minHeight: "100vh", background: embed ? "transparent" : "#faf8f5", color: "#111", fontFamily: "system-ui,-apple-system,sans-serif", padding: embed ? 16 : "48px 24px" }}>
      <div style={{ maxWidth: 960, width: "100%", margin: "0 auto" }} data-testid="book-page">{children}</div>
    </div>
  );

  if (status === "loading") return shell(<p style={{ color: "#888", textAlign: "center" }}>Loading…</p>);
  if (status === "off") return shell(
    <div style={{ textAlign: "center", paddingTop: 40 }}>
      <h1 style={{ fontSize: 24, marginBottom: 8 }}>Bookings are currently unavailable</h1>
      <p style={{ color: "#777" }}>Please check back soon.</p>
    </div>
  );

  const heading = (tenant?.event_entry && tenant.event_entry.heading) || "Book an Event";
  const subtitle = (tenant?.event_entry && tenant.event_entry.subtitle) || "Tell us about your event and we’ll take care of the rest.";

  return shell(
    <div>
      {!embed && (
        <header style={{ textAlign: "center", marginBottom: 36 }}>
          {tenant?.logo_url && (
            <img src={img(tenant.logo_url)} alt="" style={{ height: 48, width: 48, objectFit: "contain", borderRadius: 10, marginBottom: 14 }} />
          )}
          <h1 style={{ fontSize: 34, marginBottom: 6, fontWeight: 800 }}>{heading}</h1>
          <p style={{ color: "#777", fontSize: 16 }}>{subtitle}</p>
        </header>
      )}

      {packages.length > 0 ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 22 }} data-testid="book-page-packages">
            {packages.map((p) => {
              const cover = (p.images && p.images[0]) ? img(p.images[0]) : (tenant?.event_entry?.image ? img(tenant.event_entry.image) : "");
              return (
                <div key={p.id} data-morrcart-book data-mc-pkg={p.id} data-testid={`book-page-package-${p.id}`}
                  style={{ border: "1px solid #ececec", borderRadius: 14, overflow: "hidden", background: "#fff", cursor: "pointer", display: "flex", flexDirection: "column", boxShadow: "0 1px 3px rgba(0,0,0,.05)", transition: "transform .2s ease, box-shadow .2s ease" }}
                  onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 10px 28px rgba(0,0,0,.12)"; }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,.05)"; }}>
                  <div style={{ height: 170, background: cover ? `#eee url('${cover}') center/cover` : brand, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 44 }}>
                    {!cover && "📅"}
                  </div>
                  <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                      <span style={{ fontWeight: 700, fontSize: 18 }}>{p.name}</span>
                      {p.price ? <span style={{ fontWeight: 700 }}>{money(p.price)}</span> : null}
                    </div>
                    {p.description && <p style={{ color: "#666", fontSize: 14, margin: 0 }}>{p.description}</p>}
                    {p.features && p.features.length > 0 && (
                      <ul style={{ margin: "2px 0 0", paddingLeft: 18, color: "#555", fontSize: 14, display: "flex", flexDirection: "column", gap: 3 }}>
                        {p.features.slice(0, 5).map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    )}
                    <span style={{ marginTop: "auto", paddingTop: 8, fontWeight: 700, fontSize: 14, color: brand }}>Book now →</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ textAlign: "center", marginTop: 32 }}>
            <button data-morrcart-book data-testid="book-page-cta"
              style={{ background: brand, color: "#fff", border: "none", borderRadius: 999, padding: "13px 30px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
              Start a booking
            </button>
          </div>
        </>
      ) : (
        <div style={{ textAlign: "center", paddingTop: 20 }}>
          <button data-morrcart-book data-testid="book-page-cta"
            style={{ background: brand, color: "#fff", border: "none", borderRadius: 999, padding: "13px 30px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
            Book an Event
          </button>
        </div>
      )}
    </div>
  );
}
